import React, { useState, memo } from 'react';
import { Mic, BookOpen, Sparkles, TrendingUp, Calendar, Award, ArrowRight, Clock, Play, Zap, Lightbulb, CheckCircle, Flame, Target, ChevronRight, Image as ImageIcon, Upload } from 'lucide-react';
type HomePageProps = {
  onNavigate?: (page: string) => void;
  onViewStory?: (storyId: number) => void;
};
export function HomePage({
  onNavigate,
  onViewStory
}: HomePageProps) {
  const quickStats = [{
    label: 'Total Stories',
    value: '5',
    change: '+2 this week',
    icon: BookOpen,
    color: 'green',
    gradient: 'from-green-500 to-emerald-500'
  }, {
    label: 'Storybooks',
    value: '5',
    change: '+1 this week',
    icon: Sparkles,
    color: 'purple',
    gradient: 'from-purple-500 to-pink-500'
  }, {
    label: 'Recording Time',
    value: '47m',
    change: '+12m this week',
    icon: Clock,
    color: 'blue',
    gradient: 'from-blue-500 to-cyan-500'
  }, {
    label: 'Credits',
    value: '35',
    change: '15 remaining',
    icon: Zap,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-500'
  }];
  const suggestions = [{
    id: 1,
    title: 'Perfect for a storybook!',
    subtitle: '"Summer at the Lake House"',
    action: 'Create Book',
    icon: Sparkles,
    gradient: 'from-purple-500 to-pink-500',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop'
  }, {
    id: 2,
    title: 'Keep your streak alive!',
    subtitle: 'Record: "Your first job experience"',
    action: 'Record Now',
    icon: Mic,
    gradient: 'from-green-500 to-emerald-500',
    image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=300&fit=crop'
  }];
  const activityFeed = [{
    id: 1,
    text: 'You recorded "Summer Memories"',
    time: '2 hours ago',
    icon: Mic,
    color: 'green',
    action: 'View'
  }, {
    id: 2,
    text: 'Storybook "Achilles" completed',
    time: 'Yesterday',
    icon: Sparkles,
    color: 'purple',
    action: 'Open'
  }, {
    id: 3,
    text: 'Sarah viewed "Family Recipe"',
    time: '2 days ago',
    icon: Play,
    color: 'blue',
    action: 'Share'
  }, {
    id: 4,
    text: 'You earned "Storyteller" badge',
    time: '3 days ago',
    icon: Award,
    color: 'amber',
    action: 'View'
  }];
  const goals = [{
    title: 'Weekly Recording Goal',
    current: 3,
    target: 5,
    color: 'green',
    emoji: '🎤',
    reward: '+10 credits',
    daysLeft: 3
  }, {
    title: 'First Storybook Creation',
    current: 0,
    target: 1,
    color: 'purple',
    emoji: '📚',
    reward: 'Unlock templates',
    daysLeft: null
  }];
  const recentStories = [{
    id: 1,
    title: "Grandma's Secret Recipe",
    thumbnail: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=300&fit=crop',
    duration: '8:24',
    status: 'Ready',
    statusColor: 'green',
    views: 12
  }, {
    id: 2,
    title: 'Our First Road Trip',
    thumbnail: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop',
    duration: '12:35',
    status: 'New',
    statusColor: 'blue',
    views: 3
  }, {
    id: 3,
    title: 'Summer at the Lake House',
    thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
    duration: '6:18',
    status: 'Processing',
    statusColor: 'amber',
    views: 0
  }];
  return <div className="flex-1 bg-gray-100 min-h-screen" data-id="element-1069">
      <div className="px-8 py-8" data-id="element-1070">
        {/* Hero CTA Section - Increased Height */}
        <div className="relative bg-gradient-to-r from-green-500 via-green-600 to-emerald-600 rounded-3xl p-10 mb-8 shadow-xl" data-id="element-1071">
          <div className="flex items-center justify-between" data-id="element-1072">
            <div className="flex-1" data-id="element-1073">
              <div className="flex items-center gap-3 mb-4" data-id="element-1074">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium" data-id="element-1075">
                  <CheckCircle className="w-4 h-4" data-id="element-1076" />7 day streak
                </div>
              </div>
              <h1 className="text-5xl font-bold text-white mb-3" data-id="element-1077">
                Good morning, Daniel 👋
              </h1>
              <p className="text-green-100 text-xl mb-8" data-id="element-1078">
                2 more stories to reach your weekly goal
              </p>
              <div className="flex gap-3" data-id="element-1079">
                <button onClick={() => onNavigate?.('stories')} className="px-6 py-3 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-all shadow-lg flex items-center gap-2" data-id="element-1080">
                  <Mic className="w-5 h-5" data-id="element-1081" />
                  Record Story
                </button>
                <button onClick={() => onNavigate?.('create')} className="px-6 py-3 bg-green-700 text-white font-semibold rounded-xl hover:bg-green-800 transition-all flex items-center gap-2" data-id="element-1082">
                  <Sparkles className="w-5 h-5" data-id="element-1083" />
                  Create Storybook
                </button>
              </div>
            </div>
          </div>

          {/* Custom Cover Image Upload Icon */}
          <button className="absolute bottom-6 right-6 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors group" data-id="element-1084">
            <ImageIcon className="w-6 h-6 text-white group-hover:scale-110 transition-transform" data-id="element-1085" />
          </button>
        </div>

        {/* Quick Stats - Redesigned with Gradients */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-1086">
          {quickStats.map(stat => <div key={stat.label} className="relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden group" data-id="element-1087">
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity`} data-id="element-1088"></div>

              <div className="relative" data-id="element-1089">
                <div className="flex items-center justify-between mb-4" data-id="element-1090">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${stat.gradient}`} data-id="element-1091">
                    <stat.icon className="w-6 h-6 text-white" data-id="element-1092" />
                  </div>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-1093">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-gray-600 mb-1" data-id="element-1094">
                  {stat.label}
                </div>
                <div className={`text-xs font-semibold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`} data-id="element-1095">
                  {stat.change}
                </div>
              </div>
            </div>)}
        </div>

        {/* Smart Suggestions - Slightly Redesigned */}
        <div className="mb-8" data-id="element-1096">
          <div className="flex items-center gap-2 mb-4" data-id="element-1097">
            <Sparkles className="w-6 h-6 text-purple-600" data-id="element-1098" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-1099">
              Recommended for You
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-6" data-id="element-1100">
            {suggestions.map(suggestion => <div key={suggestion.id} className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer" data-id="element-1101">
                <div className="relative h-48 overflow-hidden" data-id="element-1102">
                  <img src={suggestion.image} alt={suggestion.subtitle} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-1103" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${suggestion.gradient} opacity-60`} data-id="element-1104"></div>
                  <div className="absolute inset-0 p-6 flex flex-col justify-end" data-id="element-1105">
                    <div className="text-white" data-id="element-1106">
                      <div className="text-sm font-medium mb-2 opacity-90" data-id="element-1107">
                        {suggestion.title}
                      </div>
                      <div className="text-2xl font-bold" data-id="element-1108">
                        {suggestion.subtitle}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5" data-id="element-1109">
                  <button className={`w-full py-3 bg-gradient-to-r ${suggestion.gradient} text-white font-medium rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2`} data-id="element-1110">
                    <suggestion.icon className="w-5 h-5" data-id="element-1111" />
                    {suggestion.action}
                    <ChevronRight className="w-4 h-4" data-id="element-1112" />
                  </button>
                </div>
              </div>)}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-1113">
          {/* Activity Feed with Actions */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-1114">
            <div className="flex items-center justify-between mb-6" data-id="element-1115">
              <div className="flex items-center gap-2" data-id="element-1116">
                <Clock className="w-5 h-5 text-gray-600" data-id="element-1117" />
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-1118">
                  Recent Activity
                </h3>
              </div>
              <button onClick={() => onNavigate?.('stories')} className="text-sm text-green-600 hover:text-green-700 font-medium" data-id="element-1119">
                View all
              </button>
            </div>
            <div className="space-y-3" data-id="element-1120">
              {activityFeed.map(item => <div key={item.id} className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer border border-gray-100" data-id="element-1121">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-${item.color}-100 flex-shrink-0`} data-id="element-1122">
                    <item.icon className={`w-6 h-6 text-${item.color}-600`} data-id="element-1123" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1124">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1125">
                      {item.text}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1126">{item.time}</p>
                  </div>
                  <button className={`px-4 py-2 bg-${item.color}-50 text-${item.color}-600 text-sm font-medium rounded-lg hover:bg-${item.color}-100 transition-colors`} data-id="element-1127">
                    {item.action}
                  </button>
                </div>)}
            </div>
          </div>

          {/* Goals with Rewards */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-1128">
            <div className="flex items-center gap-2 mb-6" data-id="element-1129">
              <Target className="w-5 h-5 text-gray-600" data-id="element-1130" />
              <h3 className="text-xl font-semibold text-gray-900" data-id="element-1131">
                Active Goals
              </h3>
            </div>
            <div className="space-y-5" data-id="element-1132">
              {goals.map((goal, index) => <div key={index} className={`p-5 rounded-2xl border-2 ${goal.current > 0 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-1133">
                  <div className="flex items-start justify-between mb-3" data-id="element-1134">
                    <div className="flex items-center gap-3" data-id="element-1135">
                      <span className="text-3xl" data-id="element-1136">{goal.emoji}</span>
                      <div data-id="element-1137">
                        <div className="font-semibold text-gray-900" data-id="element-1138">
                          {goal.title}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5" data-id="element-1139">
                          Reward: {goal.reward}
                        </div>
                      </div>
                    </div>
                    {goal.daysLeft && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium" data-id="element-1140">
                        {goal.daysLeft} days left
                      </span>}
                  </div>
                  <div className="mb-2" data-id="element-1141">
                    <div className="flex items-center justify-between text-sm mb-2" data-id="element-1142">
                      <span className="font-medium text-gray-900" data-id="element-1143">
                        {goal.current} of {goal.target} complete
                      </span>
                      <span className="text-gray-600" data-id="element-1144">
                        {Math.round(goal.current / goal.target * 100)}%
                      </span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden" data-id="element-1145">
                      <div className={`h-full bg-gradient-to-r from-${goal.color}-500 to-${goal.color}-600 rounded-full transition-all duration-500`} style={{
                    width: `${goal.current / goal.target * 100}%`
                  }} data-id="element-1146" />
                    </div>
                  </div>
                  {goal.current < goal.target && <button className={`w-full mt-3 py-2 bg-${goal.color}-500 text-white text-sm font-medium rounded-lg hover:bg-${goal.color}-600 transition-colors`} data-id="element-1147">
                      Continue Goal
                    </button>}
                </div>)}
            </div>
          </div>
        </div>

        {/* Recent Stories with Stats */}
        <div className="mb-8" data-id="element-1148">
          <div className="flex items-center justify-between mb-6" data-id="element-1149">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-1150">Recent Stories</h2>
            <button onClick={() => onNavigate?.('stories')} className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1" data-id="element-1151">
              View all
              <ArrowRight className="w-4 h-4" data-id="element-1152" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1153">
            {recentStories.map(story => <div key={story.id} onClick={() => onViewStory?.(story.id)} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer hover:-translate-y-1" data-id="element-1154">
                <div className="relative aspect-video overflow-hidden" data-id="element-1155">
                  <img src={story.thumbnail} alt={story.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-1156" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-1157">
                    <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center" data-id="element-1158">
                      <Play className="w-7 h-7 text-gray-900 ml-1" data-id="element-1159" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3" data-id="element-1160">
                    <span className={`px-3 py-1 bg-${story.statusColor}-500 text-white text-xs font-medium rounded-full shadow-lg`} data-id="element-1161">
                      {story.status}
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm" data-id="element-1162">
                    {story.duration}
                  </div>
                  {story.views > 0 && <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm flex items-center gap-1" data-id="element-1163">
                      <Play className="w-3 h-3" data-id="element-1164" />
                      {story.views} views
                    </div>}
                </div>
                <div className="p-4" data-id="element-1165">
                  <h3 className="font-semibold text-gray-900 line-clamp-1" data-id="element-1166">
                    {story.title}
                  </h3>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}